import { useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { keyframes, styled } from "@pigment-css/react";
import { useLocale, useTranslations } from "@acto/web-common/locales";
import { CalendarDays, CheckMark2Small, Flag } from "@acto/web-common/components";
import type { Todo } from "@acto/taskpane/core/domain";
import { Card, Header } from "@acto/taskpane/field-sales-2/components/primitives/Card";
import { daysOverdue, startOfToday, todoDueDateLabel } from "@acto/taskpane/field-sales-2/utils/todoDueLabels";

/**
 * When a committed row may be dropped. Must exceed [RowFold]'s transition, which starts a frame
 * late: dropping the row at the transition's own length ends the fold in a visible jump.
 */
export const ROW_EXIT_MS = 280;

export interface TodoChecklistRow {
  todo: Todo;
  /** False for a todo assigned to someone else — rendered read-only. */
  canComplete: boolean;
  /** Checked optimistically, undo window still open. */
  isCompleting: boolean;
  /** Committed (or already completed server-side) — struck through, checkbox filled. */
  isCompleted: boolean;
  /** True while a committed row is mid-exit; the caller controls removal. */
  isLeaving: boolean;
}

export interface TodoChecklistCardProps {
  rows: TodoChecklistRow[];
  onComplete: (todoId: string) => void;
}

const CountHeadline = styled("p")(({ theme }) => ({
  margin: 0,
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size["17"],
  fontWeight: theme.fieldSalesTwo.font.weight["500"],
  lineHeight: theme.fieldSalesTwo.font.lineHeight["130"],
  color: theme.fieldSalesTwo.colors.neutral[900],
}));

const HeaderSeparator = styled("div")(({ theme }) => ({
  marginTop: theme.fieldSalesTwo.spacing[24],
  borderBottom: `1px solid ${theme.fieldSalesTwo.colors.neutral[300]}`,
}));

// Rows inherit the Card's horizontal inset so the checkboxes align with the headline.
const Rows = styled("div")({
  display: "flex",
  flexDirection: "column",
});

// The separator sits on the clip box so it folds away with its row instead of lingering as a
// hairline. No height while the row rests, so the card follows the row's own layout exactly.
const RowClip = styled("div")<{ leaving: boolean }>(({ theme }) => ({
  overflow: "hidden",
  transition: `height ${theme.fieldSalesTwo.motion.duration.base} ${theme.fieldSalesTwo.motion.easing.emphasized}, opacity ${theme.fieldSalesTwo.motion.duration.base} ${theme.fieldSalesTwo.motion.easing.standard}`,
  "&:not(:last-child)": { borderBottom: `1px solid ${theme.fieldSalesTwo.colors.neutral[300]}` },
  "@media (prefers-reduced-motion: reduce)": {
    transition: "none",
  },
  variants: [{ props: { leaving: true }, style: { opacity: 0 } }],
}));

function RowFold({ leaving, children }: { leaving: boolean; children: ReactNode }) {
  const contentRef = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState<number>();

  useLayoutEffect(() => {
    const el = contentRef.current;
    // A cancelled fold (the completion failed) has to give the row its own height back, or it
    // stays clipped to the zero the fold left behind.
    if (!leaving || !el) {
      setHeight(undefined);
      return;
    }

    setHeight(Math.ceil(el.getBoundingClientRect().height));
    const frame = requestAnimationFrame(() => setHeight(0));
    return () => cancelAnimationFrame(frame);
  }, [leaving]);

  return (
    <RowClip role="listitem" leaving={leaving} style={height === undefined ? undefined : { height }}>
      <div ref={contentRef}>{children}</div>
    </RowClip>
  );
}

const Row = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "flex-start",
  gap: theme.fieldSalesTwo.spacing[12],
  paddingTop: theme.fieldSalesTwo.spacing[16],
  paddingBottom: theme.fieldSalesTwo.spacing[16],
}));

const Checkbox = styled("button")<{ checked: boolean }>(({ theme }) => ({
  flexShrink: 0,
  width: 22,
  height: 22,
  borderRadius: 9,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 0,
  border: `1.5px solid ${theme.fieldSalesTwo.colors.neutral[500]}`,
  background: "transparent",
  cursor: "pointer",
  WebkitTapHighlightColor: "transparent",
  transition: "background 160ms ease, border-color 160ms ease",
  color: theme.fieldSalesTwo.colors.neutral[700],
  "&:hover": { borderColor: theme.fieldSalesTwo.colors.neutral[600] },
  "&:disabled": { cursor: "default" },
  "& svg": { width: 16, height: 16, opacity: 0, transition: "opacity 160ms ease" },
  variants: [
    {
      props: { checked: true },
      style: {
        border: "none",
        background: theme.fieldSalesTwo.colors.neutral[200],
        "& svg": { opacity: 1 },
        "&:hover": { borderColor: "transparent" },
      },
    },
  ],
}));

const Body = styled("span")(({ theme }) => ({
  flex: "1 1 auto",
  minWidth: 0,
  display: "flex",
  flexDirection: "column",
  gap: theme.fieldSalesTwo.spacing[8],
}));

const Title = styled("span")<{ done: boolean }>(({ theme }) => ({
  minWidth: 0,
  overflowWrap: "anywhere",
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size[15],
  fontWeight: theme.fieldSalesTwo.font.weight[520],
  lineHeight: theme.fieldSalesTwo.font.lineHeight[148],
  color: theme.fieldSalesTwo.colors.neutral[900],
  variants: [
    {
      props: { done: true },
      style: { color: theme.fieldSalesTwo.colors.neutral[800], textDecoration: "line-through" },
    },
  ],
}));

const Meta = styled("span")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.fieldSalesTwo.spacing[8],
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size["14"],
  fontWeight: theme.fieldSalesTwo.font.weight["440"],
  color: theme.fieldSalesTwo.colors.neutral[800],
  "& svg": { width: 18, height: 18 },
}));

const fadeIn = keyframes({
  from: { opacity: 0 },
  to: { opacity: 1 },
});

const DoneBody = styled("div")(({ theme }) => ({
  animation: `${fadeIn} ${theme.fieldSalesTwo.motion.duration.fast} ${theme.fieldSalesTwo.motion.easing.standard} both`,
  "@media (prefers-reduced-motion: reduce)": {
    animation: "none",
  },
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: theme.fieldSalesTwo.spacing[8],
  padding: `${theme.fieldSalesTwo.spacing[32]} ${theme.fieldSalesTwo.spacing[24]}`,
  textAlign: "center",
  borderRadius: theme.fieldSalesTwo.radius["44"],
  background: theme.fieldSalesTwo.colors.surface.glass,
  border: `1px solid ${theme.fieldSalesTwo.colors.surface.glassBorder}`,
  boxSizing: "border-box",
}));

const DoneTitle = styled("p")(({ theme }) => ({
  margin: 0,
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size[15],
  fontWeight: theme.fieldSalesTwo.font.weight[520],
  lineHeight: theme.fieldSalesTwo.font.lineHeight[148],
  color: theme.fieldSalesTwo.colors.neutral[900],
}));

const DoneSubtitle = styled("p")(({ theme }) => ({
  margin: 0,
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size["14"],
  fontWeight: theme.fieldSalesTwo.font.weight["440"],
  lineHeight: theme.fieldSalesTwo.font.lineHeight[148],
  color: theme.fieldSalesTwo.colors.neutral[800],
}));

/**
 * The all-done confirmation. `scope` picks whose to-dos it reports: "story" for a checklist whose
 * every listed to-do is done, "own" when only the viewer's rows are and delegated ones stay open.
 */
export function TodoChecklistDone({ scope = "story" }: { scope?: "story" | "own" }) {
  const t = useTranslations("fieldSalesTwo.genuiMeetingPrep.todoChecklist");
  const own = scope === "own";
  return (
    <DoneBody role="status">
      <DoneTitle>{t(own ? "ownDoneTitle" : "doneTitle")}</DoneTitle>
      <DoneSubtitle>{t(own ? "ownDoneSubtitle" : "doneSubtitle")}</DoneSubtitle>
    </DoneBody>
  );
}

/**
 * The checklist card: checkbox + title + due/overdue meta per row under an "n open to-dos"
 * headline. Purely presentational — completion state arrives as props.
 */
export function TodoChecklistCard({ rows, onComplete }: TodoChecklistCardProps) {
  const t = useTranslations("fieldSalesTwo.genuiMeetingPrep.todoChecklist");
  const tTodos = useTranslations("fieldSalesTwo.todosList");
  const { locale } = useLocale();

  if (rows.length === 0) return null;

  const today = startOfToday();
  const openCount = rows.filter((row) => !row.isCompleted && !row.isCompleting).length;

  return (
    <Card>
      <Header>
        <CountHeadline>{t("openCount", { count: openCount })}</CountHeadline>
        <HeaderSeparator />
      </Header>
      <Rows role="list">
        {rows.map(({ todo, canComplete, isCompleting, isCompleted, isLeaving }) => {
          const checked = isCompleting || isCompleted;
          const overdueDays = todo.dueDate ? daysOverdue(todo.dueDate, today) : 0;
          const isOverdue = overdueDays > 0;
          const metaLabel = isOverdue
            ? tTodos("meta.overdueDays", { count: overdueDays })
            : todoDueDateLabel(todo.dueDate, locale);
          const title = todo.title.length === 0 ? (todo.description ?? "") : todo.title;
          return (
            <RowFold key={todo.id} leaving={isLeaving}>
              <Row>
                <Checkbox
                  type="button"
                  checked={checked}
                  aria-label={t("completeAriaLabel")}
                  disabled={!canComplete || checked}
                  onClick={() => onComplete(todo.id)}
                >
                  <CheckMark2Small aria-hidden="true" />
                </Checkbox>
                <Body>
                  <Title done={checked}>{title}</Title>
                  {metaLabel && (
                    <Meta>
                      {isOverdue ? <Flag aria-hidden="true" /> : <CalendarDays aria-hidden="true" />}
                      <span>{metaLabel}</span>
                    </Meta>
                  )}
                </Body>
              </Row>
            </RowFold>
          );
        })}
      </Rows>
    </Card>
  );
}
